import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Resource } from '../../../types';
import { useTranslation } from '../../../contexts/TranslationContext';

interface MemoryGameItem {
  front: string;
  back: string;
}

interface MemoryGameConfigurationFormProps {
  resource: Resource;
  onUpdate: (updates: Partial<Resource>) => void;
}

export const MemoryGameConfigurationForm: React.FC<MemoryGameConfigurationFormProps> = ({
  resource,
  onUpdate
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<MemoryGameItem[]>(resource.items || [
    { front: '🐔', back: 'poule' },
    { front: '🐑', back: 'mouton' },
    { front: '🐦', back: 'canari' }
  ]);

  const updateResource = (field: string, value: any) => {
    onUpdate({ [field]: value });
  };

  const addItem = () => {
    const newItems = [...items, { front: '🎯', back: 'nouveau' }];
    setItems(newItems);
    updateResource('items', newItems);
  };

  const removeItem = (index: number) => {
    const newItems = items.filter((_: MemoryGameItem, i: number) => i !== index);
    setItems(newItems);
    updateResource('items', newItems);
  };

  const updateItem = (index: number, field: 'front' | 'back', value: string) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
    updateResource('items', newItems);
  };

  const emojiSuggestions = [
    '🐔', '🐑', '🐦', '🥄', '💡', '🏠', '👒', '🍎', '🚗', '🌳',
    '⭐', '❤️', '🌈', '🎈', '🎯', '🔔', '🔑', '📚', '✏️', '🎨'
  ];

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameTitle')}
        </label>
        <input
          type="text"
          value={resource.title || t('resourceConfig.memoryGame')}
          onChange={(e) => updateResource('title', e.target.value)}
          className="input-field"
          placeholder={t('resourceConfig.enterGameTitle')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameDescription')}
        </label>
        <input
          type="text"
          value={resource.description || ''}
          onChange={(e) => updateResource('description', e.target.value)}
          className="input-field"
          placeholder={t('resourceConfig.enterGameDescription')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameItems')}
        </label>
        <div className="space-y-2">
          {items.map((item: MemoryGameItem, index: number) => (
            <div key={index} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.emoji')}
                </label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={item.front}
                    onChange={(e) => updateItem(index, 'front', e.target.value)}
                    className="input-field text-center text-lg"
                    placeholder="🎯"
                    maxLength={2}
                  />
                  <select
                    value={item.front}
                    onChange={(e) => updateItem(index, 'front', e.target.value)}
                    className="input-field text-center"
                  >
                    <option value="">Choisir...</option>
                    {emojiSuggestions.map(emoji => (
                      <option key={emoji} value={emoji}>{emoji}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.word')}
                </label>
                <input
                  type="text"
                  value={item.back}
                  onChange={(e) => updateItem(index, 'back', e.target.value)}
                  className="input-field"
                  placeholder={t('resourceConfig.enterWord')}
                />
              </div>
              
              <button
                onClick={() => removeItem(index)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title={t('resourceConfig.removeItem')}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        
        <button
          onClick={addItem}
          className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          {t('resourceConfig.addItem')}
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameSettings')}
        </label>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoStart"
              checked={resource.autoStart || false}
              onChange={(e) => updateResource('autoStart', e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="autoStart" className="text-sm text-gray-600">
              {t('resourceConfig.autoStartGame')}
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showHints"
              checked={resource.showHints !== false}
              onChange={(e) => updateResource('showHints', e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="showHints" className="text-sm text-gray-600">
              {t('resourceConfig.showHints')}
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="soundEffects"
              checked={resource.soundEffects || false}
              onChange={(e) => updateResource('soundEffects', e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="soundEffects" className="text-sm text-gray-600">
              {t('resourceConfig.soundEffects')}
            </label>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.difficultyLevel')}
        </label>
        <select
          value={resource.difficulty || 'easy'}
          onChange={(e) => updateResource('difficulty', e.target.value)}
          className="input-field"
        >
          <option value="easy">{t('resourceConfig.easy')}</option>
          <option value="medium">{t('resourceConfig.medium')}</option>
          <option value="hard">{t('resourceConfig.hard')}</option>
        </select>
      </div>
    </div>
  );
};
